import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, X, ChevronLeft, ChevronRight, Check, X as XSm, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { useNotifications } from "@/contexts/NotificationContext";
import { useLive } from "@/contexts/LiveContext";

/**
 * AlertBar — always-visible notification ribbon pinned to the top of every
 * authed page. Born Day is a scheduling + notifications app, so the user
 * asked that alerts live on-screen permanently (not hidden inside a bell).
 *
 * If there are zero notifications, this renders a discreet "no alerts"
 * resting state instead of nothing — so the bar's position never jumps.
 *
 * Multi-alert handling: paginate through with ‹ › arrows. Each alert has
 * its own ✕ dismiss; the bar tucks itself away only after the last one
 * is dismissed for the current page-load. Mark-as-read fires when the
 * user taps the alert (which also navigates to the referenced event).
 */
export default function AlertBar() {
  const { notifications, dismiss, markRead, refresh } = useNotifications();
  const { openRoom } = useLive();
  const navigate = useNavigate();
  const [idx, setIdx] = useState(0);
  const [actionBusy, setActionBusy] = useState(false);

  const list = notifications.filter((n) => n.status !== "dismissed");
  const safeIdx = Math.min(idx, Math.max(0, list.length - 1));
  const current = list[safeIdx];
  const isInvite = current?.kind === "group_invite";

  const acceptInvite = async () => {
    if (!current || !isInvite) return;
    setActionBusy(true);
    try {
      const { data } = await api.post(`/invites/${current.ref_id}/accept`);
      // Generator already dismisses the notification server-side, but mirror
      // locally so the bar updates without waiting for the next poll.
      dismiss(current.notif_id);
      refresh && refresh();
      if (data?.group_id) navigate("/groups");
    } catch (e) {
      console.warn("[alert-bar] accept failed:", e?.message || e);
    } finally {
      setActionBusy(false);
    }
  };

  const declineInvite = async () => {
    if (!current || !isInvite) return;
    setActionBusy(true);
    try {
      await api.post(`/invites/${current.ref_id}/decline`);
      dismiss(current.notif_id);
      refresh && refresh();
    } catch (e) {
      console.warn("[alert-bar] decline failed:", e?.message || e);
    } finally {
      setActionBusy(false);
    }
  };

  const openAlert = () => {
    if (!current) return;
    if (isInvite) {
      // Don't navigate when it's an invite — the inline buttons handle it.
      if (current.status === "unread") markRead(current.notif_id);
      return;
    }
    if (current.status === "unread") markRead(current.notif_id);
    if (current.kind === "live_started" && current.ref_id) {
      openRoom(current.ref_id);
      return;
    }
    if (current.ref_type === "event" && current.ref_id) {
      navigate(`/events/${current.ref_id}`);
    }
  };

  if (list.length === 0) {
    return (
      <div
        className="w-full px-3 sm:px-5 py-1.5 flex items-center gap-2 text-xs"
        style={{
          background: "color-mix(in srgb, var(--bg-soft) 75%, transparent)",
          borderBottom: "1px solid var(--border)",
          color: "var(--text-dim)",
        }}
        data-testid="alert-bar-empty"
      >
        <Bell size={12} />
        <span>No alerts. Follow an event to get notified.</span>
      </div>
    );
  }

  return (
    <div
      className="w-full px-3 sm:px-5 py-2 flex items-center gap-2"
      style={{
        background: "color-mix(in srgb, var(--primary) 12%, transparent)",
        borderBottom: "1px solid color-mix(in srgb, var(--primary) 25%, var(--border))",
      }}
      data-testid="alert-bar"
    >
      <Bell size={14} style={{ color: "var(--primary)", flexShrink: 0 }} />
      <button
        type="button"
        onClick={openAlert}
        className="flex-1 min-w-0 text-left text-sm"
        style={{ background: "transparent", border: "none", cursor: "pointer", color: "var(--text)" }}
        data-testid="alert-bar-open"
      >
        <span className="font-semibold">{current.title}</span>
        {current.body && (
          <span className="hidden sm:inline" style={{ color: "var(--text-dim)" }}>
            {" "}· {current.body}
          </span>
        )}
      </button>

      {isInvite && (
        <div className="flex items-center gap-1.5 mr-1">
          <button
            type="button"
            className="bd-btn"
            style={{ padding: "0.2rem 0.6rem", fontSize: "0.7rem" }}
            onClick={acceptInvite}
            disabled={actionBusy}
            data-testid="alert-bar-accept"
          >
            {actionBusy ? <Loader2 size={10} className="animate-spin" /> : <><Check size={10} /> Accept</>}
          </button>
          <button
            type="button"
            className="bd-btn bd-btn-ghost"
            style={{ padding: "0.2rem 0.5rem", fontSize: "0.7rem", color: "var(--text-dim)" }}
            onClick={declineInvite}
            disabled={actionBusy}
            data-testid="alert-bar-decline"
          >
            <XSm size={10} /> Decline
          </button>
        </div>
      )}

      {list.length > 1 && (
        <div className="flex items-center gap-1 bd-mono text-[10px]" style={{ color: "var(--text-dim)" }}>
          <button
            type="button"
            className="bd-btn-ghost"
            style={{ padding: "0.15rem", background: "transparent", border: "none", cursor: "pointer", color: "var(--text-dim)" }}
            onClick={() => setIdx((i) => (i - 1 + list.length) % list.length)}
            data-testid="alert-bar-prev"
            aria-label="Previous alert"
          >
            <ChevronLeft size={12} />
          </button>
          <span>{safeIdx + 1}/{list.length}</span>
          <button
            type="button"
            className="bd-btn-ghost"
            style={{ padding: "0.15rem", background: "transparent", border: "none", cursor: "pointer", color: "var(--text-dim)" }}
            onClick={() => setIdx((i) => (i + 1) % list.length)}
            data-testid="alert-bar-next"
            aria-label="Next alert"
          >
            <ChevronRight size={12} />
          </button>
        </div>
      )}

      <button
        type="button"
        onClick={() => dismiss(current.notif_id)}
        className="bd-btn-ghost"
        style={{ padding: "0.2rem", background: "transparent", border: "none", cursor: "pointer", color: "var(--text-dim)" }}
        title="Dismiss this alert"
        aria-label="Dismiss"
        data-testid="alert-bar-dismiss"
      >
        <X size={12} />
      </button>
    </div>
  );
}
